import { useCallback, useRef, useState } from 'react'

/**
 * Saved-view plumbing for a specialized report page (CC-50013 G3, CC-49732).
 *
 * WHY THIS EXISTS — the CC-49732 footgun, removed by construction.
 * `ReportViewsMenu` takes an OPTIONAL `getPageState` prop. Mount it without
 * that prop and everything still renders, saves and lists correctly — but the
 * view is stamped v:1 with `pageState: null`, so the user's quick-filters,
 * toggles and Group By silently never come back when the view is applied. That
 * is exactly what shipped on Balance Sheet / General Ledger / Trial Balance and
 * had to be fixed later in PR 163. A page author cannot tell the difference by
 * looking at the screen; only a save → apply round-trip reveals it.
 *
 * So the three moving parts are no longer assembled per page. This hook returns
 * them as one object, and every consumer spreads that object into the menu and
 * the grid. There is no ergonomic way to take the menu without also taking the
 * page state.
 *
 * Usage on a page that owns `const [filters, setFilters] = useState({...})`:
 *
 *   const view = useSavedReportView(setFilters)
 *   …
 *   <ReportViewsMenu storageKey={KEY} currentFilters={filters}
 *                    onApply={view.applyView} getPageState={view.getPageState} />
 *   …
 *   <SomeGrid key={view.gridKey} storageKey={KEY}
 *             pageStateRef={view.pageStateRef}
 *             initialPageState={view.initialPageState} />
 *
 * The remount via `key={view.gridKey}` is required, not decorative: the grid
 * reads persisted column layout and `initialPageState` on mount only, so
 * applying a view without remounting restores the filters and leaves the old
 * layout on screen.
 */
const useSavedReportView = (setFilters) => {
  // Bumped on every apply so the grid remounts and re-reads layout + page state.
  const [gridKey, setGridKey] = useState(0)
  // The grid publishes its live quick-filter/toggle state here.
  const pageStateRef = useRef(null)
  // What the grid should MOUNT with. A ref, not state: it is read during the
  // grid's own render after the key change, so it must already be current —
  // a setState here would land one render too late.
  const appliedGridStateRef = useRef(null)

  const applyView = useCallback(
    (view) => {
      // Merge rather than replace: a saved view captures the filters that
      // existed when it was saved, and a page may since have gained a filter
      // the view knows nothing about. Replacing would blank that filter to
      // undefined and send an incomplete payload to CB.
      if (view?.filters && Object.keys(view.filters).length) {
        setFilters?.((f) => ({ ...f, ...view.filters }))
      }
      // v1 views predate page state and carry none — applying one must clear
      // any state left over from a previously applied v2 view rather than
      // silently reusing it.
      appliedGridStateRef.current = view?.v >= 2 ? view.pageState?.grid || null : null
      setGridKey((k) => k + 1)
    },
    [setFilters]
  )

  const getPageState = useCallback(() => ({ grid: pageStateRef.current?.() || null }), [])

  return {
    gridKey,
    pageStateRef,
    // Read at mount time by the freshly-keyed grid.
    get initialPageState() {
      return appliedGridStateRef.current
    },
    applyView,
    getPageState,
  }
}

export default useSavedReportView
